import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { categoriesAPI } from '../../services/api';
import Button from './Button';
import Input from './Input';
import { X, Plus, Edit2, Trash2, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import './CategoryDialog.css';

const CategoryDialog = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [isAdding, setIsAdding] = useState(false);

  const { data: categories, isLoading } = useQuery(
    'categories',
    async () => {
      const response = await categoriesAPI.list();
      return response.data.data;
    },
    { enabled: isOpen }
  );

  const createMutation = useMutation(
    (data) => categoriesAPI.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('categories');
        queryClient.invalidateQueries('products');
        toast.success('Category created successfully');
        setFormData({ name: '', description: '' });
        setIsAdding(false);
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to create category');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => categoriesAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('categories');
        queryClient.invalidateQueries('products');
        toast.success('Category updated successfully');
        setEditingId(null);
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to update category');
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => categoriesAPI.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('categories');
        queryClient.invalidateQueries('products');
        toast.success('Category deleted successfully');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to delete category');
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    setFormData({ name: category.name, description: category.description || '' });
    setIsAdding(false);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ name: '', description: '' });
    setIsAdding(false);
  };

  const handleAddNew = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({ name: '', description: '' });
  };

  if (!isOpen) return null;

  return (
    <div className="category-dialog-overlay" onClick={onClose}>
      <div className="category-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="category-dialog-header">
          <h2>Manage Categories</h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="category-dialog-content">
          {isLoading ? (
            <p>Loading categories...</p>
          ) : (
            <>
              <div className="categories-list">
                {categories && categories.length > 0 ? (
                  categories.map((category) => (
                    <div
                      key={category.id}
                      className={`category-item ${editingId === category.id ? 'editing' : ''}`}
                    >
                      {editingId === category.id ? (
                        <form onSubmit={handleSubmit} className="category-form">
                          <Input
                            label="Name"
                            value={formData.name}
                            onChange={(e) =>
                              setFormData({ ...formData, name: e.target.value })
                            }
                            required
                            autoFocus
                          />
                          <Input
                            label="Description (optional)"
                            value={formData.description}
                            onChange={(e) =>
                              setFormData({ ...formData, description: e.target.value })
                            }
                          />
                          <div className="category-form-actions">
                            <Button type="submit" size="small">
                              <Check size={16} style={{ marginRight: '4px' }} />
                              Save
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="small"
                              onClick={handleCancel}
                            >
                              Cancel
                            </Button>
                          </div>
                        </form>
                      ) : (
                        <>
                          <div className="category-info">
                            <h4>
                              {category.name}
                              {category.is_default && (
                                <span className="default-badge">Default</span>
                              )}
                            </h4>
                            {category.description && (
                              <p>{category.description}</p>
                            )}
                          </div>
                          {!category.is_default && (
                            <div className="category-actions">
                              <button
                                className="icon-button"
                                onClick={() => handleEdit(category)}
                                title="Edit"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                className="icon-button danger"
                                onClick={() => handleDelete(category.id)}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="empty-message">No categories yet. Create your first one below.</p>
                )}
              </div>

              {isAdding && (
                <form onSubmit={handleSubmit} className="category-form add-form">
                  <h3>Add New Category</h3>
                  <Input
                    label="Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    autoFocus
                  />
                  <Input
                    label="Description (optional)"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                  <div className="category-form-actions">
                    <Button type="submit">
                      <Check size={16} style={{ marginRight: '4px' }} />
                      Create
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}

              {!isAdding && !editingId && (
                <Button onClick={handleAddNew} className="add-category-button">
                  <Plus size={20} style={{ marginRight: '8px' }} />
                  Add Category
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryDialog;
